import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  draggable,
  dropTargetForElements,
  monitorForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { Condition } from "../models";

const CONDITION_DRAG_TYPE = "condition-card";
const CONDITIONS_LIST_DROP_TYPE = "conditions-list";
const AUTO_SCROLL_EDGE_SIZE = 72;
const AUTO_SCROLL_MAX_SPEED = 18;

type ConditionDragData = {
  type: typeof CONDITION_DRAG_TYPE;
  conditionId: string;
  index: number;
};

type DragState = {
  draggedConditionId: string;
  sourceIndex: number;
  destinationIndex: number;
  draggedHeight: number;
};

type AutoScrollState = {
  frameId: number | null;
  speed: number;
  lastClientY: number | null;
};

type UpdateConditionPriority = (
  updatedConditionsIdAndPriority: { id: string; priority: number }[],
  updatedConditions: Condition[],
  oldConditions: Condition[],
) => void;

const isConditionDragData = (data: Record<string | symbol, unknown>): data is ConditionDragData => {
  return data.type === CONDITION_DRAG_TYPE && typeof data.conditionId === "string" && typeof data.index === "number";
};

const getDestinationIndexFromPointer = (containerElement: HTMLElement | null, clientY: number, sourceIndex: number) => {
  if (!containerElement) {
    return sourceIndex;
  }

  const draggableElements = Array.from(
    containerElement.querySelectorAll<HTMLElement>("[data-condition-draggable='true']"),
  );

  if (!draggableElements.length) {
    return sourceIndex;
  }

  let beforeIndex = draggableElements.length;

  for (let index = 0; index < draggableElements.length; index += 1) {
    const rect = draggableElements[index].getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;

    if (clientY < midpoint) {
      beforeIndex = index;
      break;
    }
  }

  const destinationIndex = sourceIndex < beforeIndex ? beforeIndex - 1 : beforeIndex;

  return Math.max(0, Math.min(destinationIndex, draggableElements.length - 1));
};

function updatePosition(arr: Condition[], fromIndex: number, toIndex: number) {
  if (fromIndex < 0 || fromIndex >= arr.length || toIndex < 0 || toIndex >= arr.length) {
    return arr;
  }

  const element = arr.splice(fromIndex, 1)[0];

  arr.splice(toIndex, 0, element);

  return arr;
}

export const useConditionsDragAndDrop = ({
  conditions,
  updateConditionPriority,
}: {
  conditions: Condition[];
  updateConditionPriority: UpdateConditionPriority;
}) => {
  const conditionsContainerRef = useRef<HTMLDivElement | null>(null);
  const autoScrollRef = useRef<AutoScrollState>({ frameId: null, speed: 0, lastClientY: null });
  const dragStateRef = useRef<DragState | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);

  const setSyncedDragState = useCallback((nextState: DragState | null) => {
    dragStateRef.current = nextState;
    setDragState(nextState);
  }, []);

  const updateDragDestination = useCallback(
    (clientY: number) => {
      const currentDragState = dragStateRef.current;

      if (!currentDragState) {
        return;
      }

      const destinationIndex = getDestinationIndexFromPointer(
        conditionsContainerRef.current,
        clientY,
        currentDragState.sourceIndex,
      );

      if (destinationIndex === currentDragState.destinationIndex) {
        return;
      }

      setSyncedDragState({
        ...currentDragState,
        destinationIndex,
      });
    },
    [setSyncedDragState],
  );

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current.frameId !== null) {
      cancelAnimationFrame(autoScrollRef.current.frameId);
    }

    autoScrollRef.current = {
      frameId: null,
      speed: 0,
      lastClientY: null,
    };
  }, []);

  const startAutoScroll = useCallback(() => {
    if (autoScrollRef.current.frameId !== null) {
      return;
    }

    const scroll = () => {
      const container = conditionsContainerRef.current;
      const { speed, lastClientY } = autoScrollRef.current;

      if (!container || speed === 0) {
        autoScrollRef.current.frameId = null;
        return;
      }

      const isAtTop = container.scrollTop <= 0;
      const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight;

      if ((speed < 0 && isAtTop) || (speed > 0 && isAtBottom)) {
        autoScrollRef.current.frameId = null;
        return;
      }

      container.scrollTop += speed;

      if (lastClientY !== null) {
        updateDragDestination(lastClientY);
      }

      autoScrollRef.current.frameId = requestAnimationFrame(scroll);
    };

    autoScrollRef.current.frameId = requestAnimationFrame(scroll);
  }, [updateDragDestination]);

  const updateAutoScroll = useCallback(
    (clientY: number) => {
      const container = conditionsContainerRef.current;

      if (!container) {
        return;
      }

      const rect = container.getBoundingClientRect();
      let speed = 0;

      if (clientY < rect.top + AUTO_SCROLL_EDGE_SIZE) {
        const distanceToTopEdge = Math.max(0, clientY - rect.top);

        speed = -Math.ceil(
          ((AUTO_SCROLL_EDGE_SIZE - distanceToTopEdge) / AUTO_SCROLL_EDGE_SIZE) * AUTO_SCROLL_MAX_SPEED,
        );
      } else if (clientY > rect.bottom - AUTO_SCROLL_EDGE_SIZE) {
        const distanceToBottomEdge = Math.max(0, rect.bottom - clientY);
        
        speed = Math.ceil(
          ((AUTO_SCROLL_EDGE_SIZE - distanceToBottomEdge) / AUTO_SCROLL_EDGE_SIZE) * AUTO_SCROLL_MAX_SPEED,
        );
      }

      autoScrollRef.current.speed = speed;
      autoScrollRef.current.lastClientY = clientY;

      if (speed === 0) {
        stopAutoScroll();
        return;
      }

      startAutoScroll();
    },
    [startAutoScroll, stopAutoScroll],
  );

  const handleOnDragEnd = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      if (destinationIndex === sourceIndex) {
        return;
      }

      const removedDefaultCondition = conditions.filter(condition => !condition.isDefault);
      const defaultCondition = conditions.filter(condition => condition.isDefault);

      const updatedConditions = updatePosition(removedDefaultCondition, sourceIndex, destinationIndex);

      const updatedConditionsIdAndPriority = updatedConditions.map(condition => ({
        id: condition.id,
        priority: condition.priority,
      }));

      updatedConditionsIdAndPriority.forEach((item, index) => {
        item.priority = updatedConditions.length - index;
      });

      updateConditionPriority(updatedConditionsIdAndPriority, [...updatedConditions, ...defaultCondition], conditions);
    },
    [conditions, updateConditionPriority],
  );

  useEffect(() => {
    const element = conditionsContainerRef.current;

    if (!element) {
      return;
    }

    return dropTargetForElements({
      element,
      getData: () => ({ type: CONDITIONS_LIST_DROP_TYPE }),
      canDrop: ({ source }) => isConditionDragData(source.data),
    });
  }, []);

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => isConditionDragData(source.data),
      onDragStart: ({ source, location }) => {
        if (!isConditionDragData(source.data)) {
          return;
        }

        const draggedHeight = source.element.getBoundingClientRect().height;
        const destinationIndex = getDestinationIndexFromPointer(
          conditionsContainerRef.current,
          location.current.input.clientY,
          source.data.index,
        );

        setSyncedDragState({
          draggedConditionId: source.data.conditionId,
          sourceIndex: source.data.index,
          destinationIndex,
          draggedHeight,
        });
      },
      onDrag: ({ location }) => {
        updateDragDestination(location.current.input.clientY);
        updateAutoScroll(location.current.input.clientY);
      },
      onDrop: ({ source, location }) => {
        stopAutoScroll();

        const currentDragState = dragStateRef.current;

        setSyncedDragState(null);

        if (!isConditionDragData(source.data) || !currentDragState) {
          return;
        }

        const wasDroppedInConditionsList = location.current.dropTargets.some(dropTarget => {
          return dropTarget.data.type === CONDITIONS_LIST_DROP_TYPE || dropTarget.data.type === CONDITION_DRAG_TYPE;
        });

        if (!wasDroppedInConditionsList) {
          return;
        }

        handleOnDragEnd(currentDragState.sourceIndex, currentDragState.destinationIndex);
      },
    });
  }, [handleOnDragEnd, setSyncedDragState, stopAutoScroll, updateAutoScroll, updateDragDestination]);

  useEffect(() => {
    return () => stopAutoScroll();
  }, [stopAutoScroll]);

  return {
    dragState,
    conditionsContainerRef,
  };
};

export const useConditionDraggable = ({
  condition,
  index,
  isDragging,
}: {
  condition: Condition;
  index: number;
  isDragging: boolean;
}) => {
  const [cardElement, setCardElement] = useState<HTMLDivElement | null>(null);
  const [dragHandleElement, setDragHandleElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!cardElement || !dragHandleElement) {
      return;
    }

    return combine(
      draggable({
        element: cardElement,
        dragHandle: dragHandleElement,
        getInitialData: () => ({
          type: CONDITION_DRAG_TYPE,
          conditionId: condition.id,
          index,
        }),
      }),
      dropTargetForElements({
        element: cardElement,
        getData: () => ({
          type: CONDITION_DRAG_TYPE,
          conditionId: condition.id,
          index,
        }),
        canDrop: ({ source }) => isConditionDragData(source.data),
      }),
    );
  }, [cardElement, dragHandleElement, condition.id, index]);

  return {
    cardRef: setCardElement,
    dragHandleProps: {
      ref: setDragHandleElement,
      onClick: (event: ReactMouseEvent<HTMLDivElement>) => event.stopPropagation(),
      sx: {
        touchAction: "none",
        cursor: isDragging ? "grabbing" : "grab",
        alignItems: "center",
      },
    },
    snapshot: { isDragging },
  };
};
